import { AuthClient, type AuthOptions } from './auth';
import { networkError, QubBaseError, toError, type Fetch } from './http';
import { QueryBuilder, RpcBuilder } from './postgrest';
import { RealtimeClient, type ChannelOptions } from './realtime';
import { StorageClient } from './storage';

export interface ClientOptions {
  auth?: AuthOptions;
  db?: { schema?: string };
  global?: { fetch?: Fetch; headers?: Record<string, string> };
}

export interface FunctionInvokeOptions {
  body?: unknown;
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  headers?: Record<string, string>;
}

/**
 * QubBase client. `url` is the project API URL (`http://localhost:4206/p/<ref>`), `key` the anon key in
 * browsers or the service_role key on trusted servers.
 */
export class QubBaseClient {
  readonly auth: AuthClient;
  readonly storage: StorageClient;
  readonly realtime: RealtimeClient;
  readonly functions: { invoke: <T = unknown>(name: string, options?: FunctionInvokeOptions) => Promise<{ data: T | null; error: QubBaseError | null; response?: Response }> };
  private readonly fetcher: Fetch;
  private readonly schemaName: string | null;

  constructor(
    readonly url: string,
    readonly key: string,
    private readonly options: ClientOptions = {},
  ) {
    if (!url) throw new Error('QubBase URL is required');
    if (!key) throw new Error('QubBase key is required');
    this.url = url.replace(/\/+$/, '');
    this.fetcher = options.global?.fetch ?? ((...args) => fetch(...args));
    this.schemaName = options.db?.schema ?? null;
    const ref = /\/p\/([a-z]{20})$/.exec(this.url)?.[1] ?? 'default';
    this.auth = new AuthClient(`${this.url}/auth/v1`, key, this.fetcher, { ...options.auth, projectRef: ref });
    this.storage = new StorageClient(`${this.url}/storage/v1`, () => this.headers(), this.fetcher);
    this.realtime = new RealtimeClient(`${this.url}/realtime/v1`, key, () => this.auth.accessToken());
    this.auth.onAuthStateChange((event, session) => {
      if (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED' || event === 'SIGNED_OUT' || event === 'MFA_CHALLENGE_VERIFIED') {
        this.realtime.setAuth(session?.access_token ?? key);
      }
    });
    this.functions = { invoke: (name, opts) => this.invoke(name, opts) };
  }

  private async headers(): Promise<Record<string, string>> {
    return { ...this.options.global?.headers, apikey: this.key, Authorization: `Bearer ${await this.auth.accessToken()}`, 'x-client-info': 'qubbase-js/0.1.0' };
  }

  /** Query a table or view: `client.from('todos').select('*').eq('done', false)`. */
  from<T = Record<string, unknown>>(table: string) {
    return new QueryBuilder<T>(this.fetcher, `${this.url}/rest/v1/${encodeURIComponent(table)}`, () => this.headers(), this.schemaName);
  }

  /** Same API, another exposed schema: `client.schema('analytics').from('events')`. */
  schema(name: string) {
    return {
      from: <T = Record<string, unknown>>(table: string) => new QueryBuilder<T>(this.fetcher, `${this.url}/rest/v1/${encodeURIComponent(table)}`, () => this.headers(), name),
      rpc: <R = unknown>(fn: string, args: Record<string, unknown> = {}, options: { head?: boolean; get?: boolean; count?: 'exact' | 'planned' | 'estimated' } = {}) =>
        new RpcBuilder<R>(this.fetcher, `${this.url}/rest/v1/rpc/${encodeURIComponent(fn)}`, () => this.headers(), name, args, options),
    };
  }

  /** Call a Postgres function: `client.rpc('search_posts', { term: 'x' })`. */
  rpc<R = unknown>(fn: string, args: Record<string, unknown> = {}, options: { head?: boolean; get?: boolean; count?: 'exact' | 'planned' | 'estimated' } = {}) {
    return new RpcBuilder<R>(this.fetcher, `${this.url}/rest/v1/rpc/${encodeURIComponent(fn)}`, () => this.headers(), this.schemaName, args, options);
  }

  channel(topic: string, options: ChannelOptions = {}) {
    return this.realtime.channel(topic, options);
  }

  removeChannel(channel: ReturnType<RealtimeClient['channel']>) {
    return this.realtime.removeChannel(channel);
  }

  removeAllChannels() {
    return this.realtime.removeAllChannels();
  }

  getChannels() {
    return this.realtime.getChannels();
  }

  private async invoke<T>(name: string, options: FunctionInvokeOptions = {}) {
    const headers: Record<string, string> = { ...(await this.headers()), ...options.headers };
    let body: BodyInit | undefined;
    if (options.body !== undefined) {
      if (typeof options.body === 'string') {
        body = options.body;
        headers['Content-Type'] ??= 'text/plain';
      } else if (options.body instanceof Blob || options.body instanceof ArrayBuffer || options.body instanceof FormData) {
        body = options.body as BodyInit;
      } else {
        body = JSON.stringify(options.body);
        headers['Content-Type'] ??= 'application/json';
      }
    }
    try {
      const res = await this.fetcher(`${this.url}/functions/v1/${name}`, { method: options.method ?? 'POST', headers, body });
      if (!res.ok) return { data: null, error: await toError(res.clone()), response: res };
      const type = res.headers.get('content-type') ?? '';
      const data = type.includes('application/json') ? await res.json() : type.startsWith('text/') ? await res.text() : await res.blob();
      return { data: data as T, error: null, response: res };
    } catch (err) {
      return { data: null, error: networkError(err) };
    }
  }
}

export function createClient(url: string, key: string, options: ClientOptions = {}) {
  return new QubBaseClient(url, key, options);
}

export { QubBaseError } from './http';
export type { Session, User, AuthChangeEvent, AuthOptions, SessionStorage } from './auth';
export type { PostgrestResponse } from './postgrest';
export type { Bucket, FileObject } from './storage';
export type { PostgresChangePayload, RealtimeChannel, ChannelStatus } from './realtime';
